/**
 * Audit service — builds and writes audit log entries.
 */

import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { AuditAction, UserRef } from '@/types';

interface AuditEntryOptions {
  audience?:    string | null;
  monthNumber?: number | null;
  weekNumber?:  number | null;
  dayNumber?:   number | null;
  recordId?:    string | null;
  fieldsChanged?:  string[];
  previousValues?: Record<string, unknown>;
  newValues?:      Record<string, unknown>;
  metadata?:       Record<string, unknown>;
}

export const auditService = {
  /**
   * Builds an audit log entry object (without writing it).
   * Used when batching writes alongside content saves.
   */
  buildEntry(
    user: UserRef,
    action: AuditAction,
    opts: AuditEntryOptions = {}
  ) {
    return {
      userId:          user.uid,
      userDisplayName: user.displayName,
      userEmail:       user.email,
      action,
      timestamp:       serverTimestamp(),
      audience:        opts.audience    ?? null,
      monthNumber:     opts.monthNumber ?? null,
      weekNumber:      opts.weekNumber  ?? null,
      dayNumber:       opts.dayNumber   ?? null,
      recordId:        opts.recordId    ?? null,
      fieldsChanged:   opts.fieldsChanged  ?? [],
      previousValues:  opts.previousValues ?? {},
      newValues:       opts.newValues      ?? {},
      metadata:        opts.metadata       ?? {},
    };
  },

  /**
   * Writes a single audit log entry immediately.
   * Use for standalone actions (login, logout, etc.).
   */
  async log(
    user: UserRef,
    action: AuditAction,
    opts: AuditEntryOptions = {}
  ): Promise<void> {
    const ref = doc(collection(db, 'auditLogs'));
    await setDoc(ref, this.buildEntry(user, action, opts));
  },
};
